import React, { useEffect, useMemo, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import ImageLoader from "../../common/ImageLoader";
import {
  CURRENCY_SIGN,
  SUPPORTED_REGIONS,
  CURRENCY_MAPPING,
  PRODUCT_VARIANT_ABBREVIATIONS,
  STATUS,
} from "../../../../shared/enums";
import {
  selectRegionalPricesData,
  fetchRegionalPrices,
  selectRegionalPricesStatus,
} from "../../../features/regionalPrices/regionalPricesSlice";
import {
  getUnitPrice,
  getFilterPrice,
  getSocksPrice,
} from "../../../features/regionalPrices/regionalPricesHelpers";

import SidebarItem from "./SidebarItem";
import { useHeaderPrices } from "../../../hooks";
import { getAir3BuyUrl } from "../../../static-data/shopUrls";

import milaAir3 from "assets/images/landingAir/mila-air-3-alt.jpg";
import milaAir3Webp from "assets/images/landingAir/mila-air-3-alt.jpg?as=webp";

import milaAirMini from "assets/images/landingAir/mila-air-mini.jpg";
import milaAirMiniWebp from "assets/images/landingAir/mila-air-mini.jpg?as=webp";

import accessory01 from "assets/images/landingAir/accessory-img-01.jpg";
import accessory01Webp from "assets/images/landingAir/accessory-img-01.jpg?as=webp";

import accessory03 from "assets/images/landingAir/accessory-img-03.jpg";
import accessory03Webp from "assets/images/landingAir/accessory-img-03.jpg?as=webp";

const SidebarMenu = forwardRef(({ regional }, ref) => {
  const dispatch = useDispatch();
  const regionalPricesStatus = useSelector(selectRegionalPricesStatus);
  const { air, airMini, milaFilter, milaSocks } = useHeaderPrices(regional);
  const air3BuyUrl = getAir3BuyUrl(regional);
  const isAu = regional === SUPPORTED_REGIONS.AU;
  const currencySign = CURRENCY_SIGN[regional] || CURRENCY_SIGN.US;
  const currencyCode = CURRENCY_MAPPING[regional] || CURRENCY_MAPPING.US;
  const regionalPrices = useSelector(selectRegionalPricesData);
  const airPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER,
  );
  const airMiniPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER_MINI,
  );
  const airFilterPrice = getFilterPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER,
  );
  const airMiniFilterPrice = getFilterPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER_MINI,
  );
  const airSocksPrice = getSocksPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.SOCKS,
  );
  const isGb = regional === SUPPORTED_REGIONS.GB;

  useEffect(() => {
    if (!isGb) return undefined;
    if (regionalPricesStatus === STATUS.IDLE) {
      dispatch(fetchRegionalPrices({ region: regional }));
    }
    return undefined;
  }, [dispatch, isGb, regional, regionalPricesStatus]);

  const sidebarContent = useMemo(() => {
    return (
      <div className="header__sidebar order-list" ref={ref}>
        <SidebarItem title="Choose your Mila">
          <div className="mila-list">
            <div className="mila-item">
              <ImageLoader
                className="mila-item__image img-relative"
                placeholderAspectRatio={1 / 1}
                image={milaAir3}
                webp={milaAir3Webp}
                alt={"Mila Air"}
              />
              <div className="mila-item__content">
                <div className="mila-item__heading">
                  <h3 className="mila-item__title">Mila Air 3</h3>
                  <p className="mila-item__price">
                    {`Starting at ${currencySign}${airPrice || ""} ${currencyCode}`}
                    <span>with Auto-Refill</span>
                  </p>
                </div>
                <a
                  href={air3BuyUrl}
                  className="button button--primary button--sm"
                >
                  Buy now
                </a>
              </div>
            </div>
            {!isAu && (
              <div className="mila-item">
                <ImageLoader
                  className="mila-item__image img-relative"
                  placeholderAspectRatio={1 / 1}
                  image={milaAirMini}
                  webp={milaAirMiniWebp}
                  alt={"Mila Air Mini"}
                />
                <div className="mila-item__content">
                  <div className="mila-item__heading">
                    <h3 className="mila-item__title">Mila Air Mini</h3>
                    <p className="mila-item__price">
                      {`Starting at ${currencySign}${airMiniPrice || ""} ${currencyCode}`}
                      <span>with Auto-Refill</span>{" "}
                    </p>
                  </div>
                  <a
                    href="https://buy.milacares.com/"
                    className="button button--primary button--sm"
                  >
                    {regional === SUPPORTED_REGIONS.GB ? "Preorder" : "Buy now"}
                  </a>
                </div>
              </div>
            )}
          </div>
        </SidebarItem>

        <SidebarItem title="Buy accessories">
          <div className="mila-list">
            <div className="mila-item mila-item--filter">
              <ImageLoader
                className="mila-item__image img-relative"
                placeholderAspectRatio={1 / 1}
                image={accessory01}
                webp={accessory01Webp}
                alt={"Mila Air Filters"}
              />
              <div className="mila-item__content">
                <div className="mila-item__heading">
                  <h3 className="mila-item__title">Mila Air Filter</h3>
                  <p className="mila-item__price">
                    {`Starting at ${currencySign}${airFilterPrice || ""} ${currencyCode}`}
                    {milaFilter.del && (
                      <del>
                        {CURRENCY_SIGN[regional]}
                        {milaFilter.del}
                      </del>
                    )}
                  </p>
                  {/* <button
                      type="button"
                      className="button button--white button--sm button--shadow-dark"
                    >
                      Filter type <IconAngleDown />
                    </button> */}
                </div>
                <a
                  href="https://shop.milacares.com/filters"
                  className="button button--primary button--sm"
                >
                  Buy now
                </a>
              </div>
            </div>

            <div className="mila-item mila-item--filter">
              <ImageLoader
                className="mila-item__image img-relative"
                placeholderAspectRatio={1 / 1}
                image={accessory03}
                webp={accessory03Webp}
                alt={"Mila Air Socks"}
              />
              <div className="mila-item__content">
                <div className="mila-item__heading">
                  <h3 className="mila-item__title">Mila Socks (5 pack)</h3>
                  <p className="mila-item__price">
                    {`Starting at ${currencySign}${airSocksPrice || ""} ${currencyCode}`}
                    {milaSocks.del && (
                      <del>
                        {CURRENCY_SIGN[regional]}
                        {milaSocks.del}
                      </del>
                    )}
                  </p>
                </div>
                <a
                  href="https://milacares.com/buy"
                  className="button button--primary button--sm"
                >
                  Buy now
                </a>
              </div>
            </div>
          </div>
        </SidebarItem>
      </div>
    );
  }, [air, airMini, milaFilter, milaSocks, regional, air3BuyUrl, isAu]);

  return sidebarContent;
});

export default SidebarMenu;
